import React, { useState, useEffect, useRef } from 'react';
import DemoForm from './DemoForm';

const Arch = () => {
  const [navScrolled, setNavScrolled] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div style={{ fontFamily: "'Cormorant', serif", background: '#fff', color: '#0a0a0a', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Figtree:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --black: #0a0a0a;
          --white: #ffffff;
          --accent: #1a56f0;
          --accent-dark: #1241c4;
          --mid: #6b7280;
          --light: #f4f4f5;
          --border: #e4e4e7;
          --card-bg: #fafafa;
        }

        body { -webkit-font-smoothing: antialiased; }

        /* NAV */
        .arch-nav {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 100;
          padding: 0 48px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: background 0.3s, border-bottom 0.3s, backdrop-filter 0.3s;
        }
        .arch-nav.scrolled {
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--border);
        }
        .nav-logo {
          font-family: 'Cormorant', serif;
          font-weight: 800;
          font-size: 18px;
          letter-spacing: -0.5px;
          color: var(--black);
          text-decoration: none;
        }
        .nav-links {
          display: flex;
          gap: 36px;
          list-style: none;
          align-items: center;
        }
        .nav-links a {
          font-family: 'Figtree', sans-serif;
          font-size: 14px;
          font-weight: 400;
          color: #444;
          text-decoration: none;
          transition: color 0.2s;
        }
        .nav-links a:hover { color: var(--black); }
        .nav-cta {
          background: var(--black) !important;
          color: var(--white) !important;
          padding: 10px 20px !important;
          border-radius: 8px !important;
          font-weight: 500 !important;
          font-size: 14px !important;
          transition: background 0.2s !important;
        }
        .nav-cta:hover { background: #222 !important; }

        /* HERO */
        .hero {
          min-height: 100vh;
          padding: 120px 48px 80px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
          max-width: 1280px;
          margin: 0 auto;
        }
        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--light);
          border: 1px solid var(--border);
          border-radius: 100px;
          padding: 6px 14px;
          font-family: 'Figtree', sans-serif;
          font-size: 12px;
          font-weight: 500;
          color: #555;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          margin-bottom: 28px;
        }
        .eyebrow-dot {
          width: 6px; height: 6px;
          background: var(--accent);
          border-radius: 50%;
          animation: pulse-dot 2s infinite;
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .hero-headline {
          font-family: 'Cormorant', serif;
          font-size: clamp(56px, 6.5vw, 96px);
          font-weight: 600;
          line-height: 0.97;
          letter-spacing: -1px;
          color: var(--black);
          margin-bottom: 24px;
        }
        .hero-headline span {
          color: var(--accent);
        }
        .hero-sub {
          font-family: 'Figtree', sans-serif;
          font-size: 17px;
          font-weight: 300;
          color: #555;
          line-height: 1.7;
          max-width: 460px;
          margin-bottom: 40px;
        }
        .hero-actions {
          display: flex;
          gap: 12px;
          align-items: center;
        }
        .btn-primary {
          background: var(--black);
          color: var(--white);
          border: none;
          padding: 14px 28px;
          border-radius: 10px;
          font-family: 'Figtree', sans-serif;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s, transform 0.15s;
        }
        .btn-primary:hover {
          background: #1a1a1a;
          transform: translateY(-1px);
        }
        .btn-primary svg { transition: transform 0.2s; }
        .btn-primary:hover svg { transform: translateX(3px); }
        .btn-secondary {
          background: transparent;
          color: var(--black);
          border: 1.5px solid var(--border);
          padding: 14px 24px;
          border-radius: 10px;
          font-family: 'Figtree', sans-serif;
          font-size: 15px;
          font-weight: 400;
          cursor: pointer;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #555;
          transition: border-color 0.2s, color 0.2s, transform 0.15s;
        }
        .btn-secondary:hover {
          border-color: #bbb;
          color: var(--black);
          transform: translateY(-1px);
        }

        /* HERO CARDS */
        .hero-visual {
          position: relative;
          height: 460px;
        }
        .metric-card {
          position: absolute;
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 20px 24px;
          box-shadow: 0 4px 24px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04);
          min-width: 200px;
          transition: box-shadow 0.3s;
          animation: float-card 0.6s ease both;
        }
        .metric-card:hover {
          box-shadow: 0 8px 40px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06);
        }
        .card-1 { top: 0; right: 0; animation-delay: 0.1s; }
        .card-2 { top: 140px; right: 60px; animation-delay: 0.25s; }
        .card-3 { top: 290px; right: 10px; animation-delay: 0.4s; }

        @keyframes float-card {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .card-icon-wrap {
          width: 36px; height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
        }
        .card-icon-wrap.blue  { background: #e8f0fe; }
        .card-icon-wrap.green { background: #e6f4ea; }
        .card-icon-wrap.amber { background: #fef9e7; }

        .card-label {
          font-family: 'Figtree', sans-serif;
          font-size: 12px;
          font-weight: 500;
          color: var(--mid);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 8px;
        }
        .card-value {
          font-family: 'Cormorant', serif;
          font-size: 28px;
          font-weight: 700;
          color: var(--black);
          letter-spacing: -1px;
          line-height: 1;
          margin-bottom: 4px;
        }
        .card-sub {
          font-family: 'Figtree', sans-serif;
          font-size: 12px;
          color: var(--mid);
        }
        .card-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: #e6f4ea;
          color: #1e7e34;
          font-size: 11px;
          font-weight: 500;
          padding: 3px 8px;
          border-radius: 100px;
          margin-top: 8px;
        }

        /* SECTION GENERIC */
        .section {
          max-width: 1280px;
          margin: 0 auto;
          padding: 100px 48px;
        }
        .section-tag {
          font-family: 'Figtree', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--accent);
          margin-bottom: 16px;
        }
        .section-title {
          font-family: 'Cormorant', serif;
          font-size: clamp(36px, 4vw, 60px);
          font-weight: 600;
          letter-spacing: -0.5px;
          line-height: 1.02;
          color: var(--black);
          margin-bottom: 20px;
        }
        .section-body {
          font-family: 'Figtree', sans-serif;
          font-size: 17px;
          font-weight: 300;
          color: #555;
          line-height: 1.7;
          max-width: 540px;
        }

        /* DIVIDER */
        .divider {
          height: 1px;
          background: var(--border);
          max-width: 1280px;
          margin: 0 auto;
        }

        /* PROBLEM */
        .problem-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .problem-items {
          display: flex;
          flex-direction: column;
          gap: 24px;
          margin-top: 8px;
        }
        .problem-item {
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }
        .problem-num {
          font-family: 'Cormorant', serif;
          font-size: 12px;
          font-weight: 700;
          color: #ccc;
          min-width: 24px;
          padding-top: 3px;
        }
        .problem-text {
          font-family: 'Figtree', sans-serif;
          font-size: 15px;
          color: #444;
          line-height: 1.6;
        }
        .problem-text strong {
          color: var(--black);
          font-weight: 500;
        }
        .problem-visual {
          background: var(--light);
          border: 1px solid var(--border);
          border-radius: 20px;
          padding: 32px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .chaos-item {
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .chaos-icon {
          width: 32px; height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .chaos-label {
          font-family: 'Figtree', sans-serif;
          font-size: 13px;
          color: #444;
          font-weight: 400;
        }
        .chaos-label strong { color: var(--black); font-weight: 500; }
        .chaos-status {
          margin-left: auto;
          font-size: 11px;
          padding: 3px 8px;
          border-radius: 100px;
          font-weight: 500;
        }
        .status-red { background: #fee2e2; color: #b91c1c; }
        .status-yellow { background: #fef9c3; color: #a16207; }

        /* HOW IT WORKS */
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1px;
          background: var(--border);
          border: 1px solid var(--border);
          border-radius: 16px;
          overflow: hidden;
          margin-top: 56px;
        }
        .step {
          background: var(--white);
          padding: 36px 28px;
          position: relative;
        }
        .step:first-child { border-radius: 16px 0 0 16px; }
        .step:last-child  { border-radius: 0 16px 16px 0; }
        .step-num {
          font-family: 'Cormorant', serif;
          font-size: 11px;
          font-weight: 700;
          color: #ccc;
          letter-spacing: 0.08em;
          margin-bottom: 20px;
        }
        .step-icon {
          width: 44px; height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          background: var(--light);
        }
        .step-title {
          font-family: 'Cormorant', serif;
          font-size: 17px;
          font-weight: 700;
          color: var(--black);
          margin-bottom: 10px;
          letter-spacing: -0.3px;
        }
        .step-desc {
          font-family: 'Figtree', sans-serif;
          font-size: 14px;
          color: #666;
          line-height: 1.6;
        }
        .step-arrow {
          position: absolute;
          right: -14px;
          top: 50%;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
        }

        /* WHO ITS FOR */
        .for-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-top: 52px;
        }
        .for-card {
          background: var(--card-bg);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 32px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .for-card:hover {
          border-color: #c5c5c5;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
        }
        .for-card-icon {
          width: 48px; height: 48px;
          border-radius: 12px;
          background: var(--white);
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }
        .for-card-title {
          font-family: 'Cormorant', serif;
          font-size: 18px;
          font-weight: 700;
          letter-spacing: -0.4px;
          margin-bottom: 10px;
        }
        .for-card-desc {
          font-family: 'Figtree', sans-serif;
          font-size: 14px;
          color: #666;
          line-height: 1.65;
        }

        /* WHY DIFFERENT */
        .diff-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .diff-list {
          display: flex;
          flex-direction: column;
          gap: 0;
          margin-top: 8px;
          border: 1px solid var(--border);
          border-radius: 14px;
          overflow: hidden;
        }
        .diff-item {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 22px 24px;
          border-bottom: 1px solid var(--border);
          background: var(--white);
          transition: background 0.15s;
        }
        .diff-item:last-child { border-bottom: none; }
        .diff-item:hover { background: var(--light); }
        .diff-check {
          width: 22px; height: 22px;
          border-radius: 50%;
          background: #e8f0fe;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .diff-text {
          font-family: 'Figtree', sans-serif;
          font-size: 15px;
          color: #333;
          line-height: 1.5;
        }
        .diff-text strong { color: var(--black); font-weight: 500; }

        /* TRUST */
        .trust-strip {
          background: var(--black);
          padding: 100px 48px;
        }
        .trust-inner {
          max-width: 1280px;
          margin: 0 auto;
        }
        .trust-title {
          font-family: 'Cormorant', serif;
          font-size: clamp(32px, 3.5vw, 52px);
          font-weight: 600;
          color: var(--white);
          letter-spacing: -0.5px;
          margin-bottom: 52px;
          max-width: 600px;
        }
        .trust-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }
        .trust-pill {
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 100px;
          padding: 10px 18px;
          font-family: 'Figtree', sans-serif;
          font-size: 14px;
          color: rgba(255,255,255,0.85);
          font-weight: 400;
        }

        /* CTA */
        .cta-section {
          max-width: 1280px;
          margin: 0 auto;
          padding: 120px 48px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .cta-title {
          font-family: 'Cormorant', serif;
          font-size: clamp(42px, 5vw, 72px);
          font-weight: 600;
          letter-spacing: -0.5px;
          line-height: 1.0;
          color: var(--black);
        }
        .cta-body {
          font-family: 'Figtree', sans-serif;
          font-size: 16px;
          color: #555;
          line-height: 1.7;
          margin-bottom: 36px;
        }
        .cta-note {
          font-family: 'Figtree', sans-serif;
          font-size: 13px;
          color: #aaa;
          margin-top: 16px;
        }

        /* FOOTER */
        .footer {
          border-top: 1px solid var(--border);
          padding: 48px 48px;
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .footer-logo {
          font-family: 'Cormorant', serif;
          font-weight: 800;
          font-size: 16px;
        }
        .footer-links {
          display: flex;
          gap: 32px;
          list-style: none;
        }
        .footer-links a {
          font-family: 'Figtree', sans-serif;
          font-size: 13px;
          color: #888;
          text-decoration: none;
          transition: color 0.2s;
        }
        .footer-links a:hover { color: var(--black); }
        .footer-copy {
          font-family: 'Figtree', sans-serif;
          font-size: 13px;
          color: #aaa;
        }

        /* HAMBURGER */
        .hamburger {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        /* MOBILE DROPDOWN */
        .mobile-menu {
          display: none;
          position: absolute;
          top: 64px;
          left: 0;
          right: 0;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--border);
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          padding: 8px 24px 24px;
          flex-direction: column;
          z-index: 99;
        }
        .mobile-menu-link {
          font-family: 'Figtree', sans-serif;
          font-size: 16px;
          font-weight: 400;
          color: #444;
          text-decoration: none;
          display: block;
          padding: 14px 0;
          border-bottom: 1px solid var(--border);
          transition: color 0.2s;
        }
        .mobile-menu-link:hover { color: var(--black); }
        .mobile-menu-cta-btn {
          font-family: 'Figtree', sans-serif;
          font-size: 15px;
          font-weight: 500;
          background: var(--black);
          color: var(--white);
          text-decoration: none;
          display: block;
          padding: 14px;
          border-radius: 10px;
          text-align: center;
          margin-top: 16px;
          transition: background 0.2s;
        }
        .mobile-menu-cta-btn:hover { background: #222; }

        @media (max-width: 767px) {
          .nav-links { display: none; }
          .hamburger { display: flex; }
          .mobile-menu.is-open { display: flex; }
        }

        @media (max-width: 900px) {
          .hero, .problem-grid, .diff-grid, .cta-section {
            grid-template-columns: 1fr;
            gap: 48px;
          }
          .hero-visual { height: 300px; }
          .steps-grid { grid-template-columns: 1fr 1fr; }
          .for-grid { grid-template-columns: 1fr; }
          .arch-nav { padding: 0 24px; }
          .section, .hero { padding-left: 24px; padding-right: 24px; }
          .trust-strip { padding: 60px 24px; }
          .footer { flex-direction: column; gap: 24px; text-align: center; padding: 40px 24px; }
          .step-arrow { display: none; }
        }
      `}</style>

      {/* NAV */}
      <nav className={`arch-nav ${navScrolled ? 'scrolled' : ''}`} ref={navRef}>
        <a href="#top" className="nav-logo">Arch</a>

        {/* Desktop links — hidden below 768px via CSS */}
        <ul className="nav-links">
          <li><a href="#solution">Solution</a></li>
          <li><a href="#how-it-works">How It Works</a></li>
          <li><a href="#security">Security</a></li>
          <li><a href="#contact" className="nav-cta" onClick={() => setShowDemo(true)}>Request Demo</a></li>
        </ul>

        {/* Hamburger — visible below 768px via CSS */}
        <button
          className="hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M3 6h16M3 11h16M3 16h16" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Mobile dropdown */}
        <div className={`mobile-menu${menuOpen ? ' is-open' : ''}`}>
          <a href="#solution" className="mobile-menu-link" onClick={() => setMenuOpen(false)}>Solution</a>
          <a href="#how-it-works" className="mobile-menu-link" onClick={() => setMenuOpen(false)}>How It Works</a>
          <a href="#security" className="mobile-menu-link" onClick={() => setMenuOpen(false)}>Security</a>
          <a
            href="#contact"
            className="mobile-menu-cta-btn"
            onClick={() => { setMenuOpen(false); setShowDemo(true); }}
          >
            Request Demo
          </a>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ background: '#fff' }}>
        <div className="hero">
          <div>
            <h1 className="hero-headline">
              Audit-ready<br />
              NIL payments.<br />
              <span>Built for compliance.</span>
            </h1>
            <p className="hero-sub">
              Enterprise platform for managing NIL contracts, payments, and compliance — with complete transparency and legal defensibility.
            </p>
            <div className="hero-actions">
              <a href="#contact" className="btn-primary" onClick={() => setShowDemo(true)}>
                Request a Demo
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7h10M7 2l5 5-5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a href="#how-it-works" className="btn-secondary">
                See how it works
              </a>
            </div>
          </div>

          <div className="hero-visual">
            {/* Card 1 - Payment Ledger */}
            <div className="metric-card card-1" style={{ width: 230 }}>
              <div className="card-icon-wrap blue">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect x="1" y="3" width="16" height="12" rx="2" stroke="#1a56f0" strokeWidth="1.5"/>
                  <path d="M1 7h16" stroke="#1a56f0" strokeWidth="1.5"/>
                  <path d="M4 11h4" stroke="#1a56f0" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="card-label">Payment Ledger</div>
              <div className="card-value">$847K</div>
              <div className="card-sub">Total disbursed · 2024</div>
              <div className="card-badge">
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <path d="M1 5l2-2 2 2 2-3" stroke="#1e7e34" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
                +12% this month
              </div>
            </div>

            {/* Card 2 - Contracts */}
            <div className="metric-card card-2" style={{ width: 210 }}>
              <div className="card-icon-wrap green">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M4 1h7l4 4v12H4V1z" stroke="#16a34a" strokeWidth="1.5" strokeLinejoin="round"/>
                  <path d="M11 1v4h4" stroke="#16a34a" strokeWidth="1.5"/>
                  <path d="M6 9h6M6 12h4" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="card-label">Contracts</div>
              <div className="card-value">234</div>
              <div className="card-sub">Active agreements</div>
            </div>

            {/* Card 3 - Compliance */}
            <div className="metric-card card-3" style={{ width: 220 }}>
              <div className="card-icon-wrap amber">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 1l2.5 5.5L17 7.5l-4 3.9 1 5.6L9 14.5l-5 2.5 1-5.6-4-3.9 5.5-1L9 1z" stroke="#d97706" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="card-label">Compliance Score</div>
              <div className="card-value">100%</div>
              <div className="card-sub">Audit ready</div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* PROBLEM */}
      <section id="solution" className="section">
        <div className="problem-grid">
          <div>
            <div className="section-tag">The Problem</div>
            <h2 className="section-title">NIL payments are a compliance nightmare</h2>
            <p className="section-body" style={{ marginBottom: 36 }}>
              Collectives are disbursing millions through spreadsheets, wire transfers, and Venmo. One audit can expose everything.
            </p>
            <div className="problem-items">
              {[
                { n: '01', t: 'No audit trail', d: 'Payments made outside any system — impossible to reconstruct when regulators ask.' },
                { n: '02', t: 'Compliance gaps', d: 'State laws, NCAA rules, and IRS requirements change constantly. Most platforms ignore this.' },
                { n: '03', t: 'Legal exposure', d: 'Unsigned or missing contracts leave organizations personally liable.' },
                { n: '04', t: 'Zero visibility', d: 'Donors and boards have no clear view into where money goes or why.' },
              ].map(item => (
                <div className="problem-item" key={item.n}>
                  <span className="problem-num">{item.n}</span>
                  <div className="problem-text">
                    <strong>{item.t} — </strong>{item.d}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="problem-visual">
            {[
              { icon: '📋', label: 'Contract #A-2241', sub: 'Missing athlete signature', status: 'Missing', type: 'red', bg: '#fee2e2' },
              { icon: '💸', label: 'Payment $12,500', sub: 'No supporting contract', status: 'Unlinked', type: 'red', bg: '#fee2e2' },
              { icon: '📊', label: 'Q3 Compliance Report', sub: 'Overdue 14 days', status: 'Overdue', type: 'yellow', bg: '#fef3c7' },
              { icon: '📁', label: 'Audit Request', sub: '43 documents pending', status: 'At Risk', type: 'red', bg: '#fee2e2' },
            ].map((item, i) => (
              <div className="chaos-item" key={i}>
                <div className="chaos-icon" style={{ background: item.bg, fontSize: 16 }}>{item.icon}</div>
                <div>
                  <div className="chaos-label"><strong>{item.label}</strong></div>
                  <div className="chaos-label" style={{ color: '#888', marginTop: 2 }}>{item.sub}</div>
                </div>
                <div className={`chaos-status status-${item.type}`}>{item.status}</div>
              </div>
            ))}
            <div style={{ textAlign: 'center', paddingTop: 8, fontFamily: 'Figtree', fontSize: 13, color: '#aaa' }}>
              Without Arch — chaos compounds
            </div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="section">
        <div className="section-tag">How It Works</div>
        <h2 className="section-title">Four steps to full compliance</h2>
        <p className="section-body">From contract to audit report — everything flows through one system, with a complete record at every step.</p>

        <div className="steps-grid">
          {[
            {
              n: '01', title: 'Contracts',
              desc: 'Upload, send for e-signature, and store all NIL agreements with version history and athlete acknowledgment.',
              icon: (
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M5 2h9l5 5v14H5V2z" stroke="#1a56f0" strokeWidth="1.5" strokeLinejoin="round"/>
                  <path d="M14 2v5h5" stroke="#1a56f0" strokeWidth="1.5"/>
                  <path d="M8 11h6M8 14h4" stroke="#1a56f0" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              )
            },
            {
              n: '02', title: 'Payments',
              desc: 'Initiate payments tied directly to specific contracts. Every disbursement is traceable to its source agreement.',
              icon: (
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <rect x="2" y="5" width="18" height="13" rx="2" stroke="#16a34a" strokeWidth="1.5"/>
                  <path d="M2 9h18" stroke="#16a34a" strokeWidth="1.5"/>
                  <circle cx="6" cy="13" r="1.5" fill="#16a34a"/>
                </svg>
              )
            },
            {
              n: '03', title: 'Ledger',
              desc: 'A living, immutable record of every contract, payment, and approval — organized for instant retrieval.',
              icon: (
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M4 4h14v14H4z" stroke="#d97706" strokeWidth="1.5" strokeLinejoin="round"/>
                  <path d="M4 8h14M4 12h14M8 8v10" stroke="#d97706" strokeWidth="1.5"/>
                </svg>
              )
            },
            {
              n: '04', title: 'Reports',
              desc: 'Generate audit-ready compliance reports on demand. One click from payment history to board-ready documentation.',
              icon: (
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M3 20V8l7-6 7 6v12H3z" stroke="#7c3aed" strokeWidth="1.5" strokeLinejoin="round"/>
                  <path d="M9 20v-6h4v6" stroke="#7c3aed" strokeWidth="1.5"/>
                  <path d="M8 11l2 2 4-4" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )
            },
          ].map((step, i) => (
            <div className="step" key={step.n} style={{ position: 'relative' }}>
              <div className="step-num">STEP {step.n}</div>
              <div className="step-icon">{step.icon}</div>
              <div className="step-title">{step.title}</div>
              <div className="step-desc">{step.desc}</div>
              {i < 3 && (
                <div className="step-arrow">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5h6M5 2l3 3-3 3" stroke="#aaa" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <div className="divider" />

      {/* WHO ITS FOR */}
      <section className="section">
        <div className="section-tag">Who It's For</div>
        <h2 className="section-title">Built for every side of NIL</h2>
        <div className="for-grid">
          {[
            {
              title: 'NIL Collectives',
              desc: 'Manage dozens of athlete agreements, track all disbursements, and walk into any audit with confidence.',
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="9" cy="7" r="3" stroke="#1a56f0" strokeWidth="1.5"/>
                  <circle cx="15" cy="7" r="3" stroke="#1a56f0" strokeWidth="1.5"/>
                  <path d="M3 19c0-3.3 2.7-6 6-6h6c3.3 0 6 2.7 6 6" stroke="#1a56f0" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              )
            },
            {
              title: 'Athlete Agencies',
              desc: 'Centralize contracts across your entire client roster. Never lose a deal or miss a payment deadline.',
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="4" stroke="#16a34a" strokeWidth="1.5"/>
                  <path d="M5 20c0-3.9 3.1-7 7-7s7 3.1 7 7" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              )
            },
            {
              title: 'Booster & Donor Organizations',
              desc: 'Know exactly where every dollar goes. Provide donors with transparent, real-time visibility into fund usage.',
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2l2.5 7H22l-6 4.4 2.3 7-6.3-4.6-6.3 4.6 2.3-7L2 9h7.5L12 2z" stroke="#d97706" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
              )
            },
            {
              title: 'Compliance & Ops Teams',
              desc: 'Replace manual checklists with a system that enforces compliance by design — not by reminder.',
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12l2 2 4-4" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 3l7 4v5c0 4.4-3 8.5-7 10C8 21.5 5 17.4 5 13V7l7-4z" stroke="#7c3aed" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
              )
            },
          ].map(card => (
            <div className="for-card" key={card.title}>
              <div className="for-card-icon">{card.icon}</div>
              <div className="for-card-title">{card.title}</div>
              <div className="for-card-desc">{card.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="divider" />

      {/* WHY DIFFERENT */}
      <section className="section">
        <div className="diff-grid">
          <div>
            <div className="section-tag">Why Arch</div>
            <h2 className="section-title">Infrastructure, not hype</h2>
            <p className="section-body">
              Most NIL tools are built for marketing and deal discovery. Arch is built for the hard part — the money, the contracts, and the audits.
            </p>
          </div>
          <div className="diff-list">
            {[
              { t: 'Not a deal marketplace', d: "We don't match brands with athletes. We manage the compliance and payments after deals are made." },
              { t: 'Not a marketing platform', d: 'No follower counts, no social analytics. Just contracts, payments, and records.' },
              { t: 'Built for audits', d: 'Every action is logged. Every document is versioned. Nothing disappears.' },
              { t: 'Payment-first architecture', d: 'Payments are linked to contracts at the database level — not manually reconciled after the fact.' },
              { t: 'Compliance by design', d: "Rule changes are reflected in the product, not in a knowledge base article you'll never read." },
            ].map(item => (
              <div className="diff-item" key={item.t}>
                <div className="diff-check">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="#1a56f0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="diff-text">
                  <strong>{item.t} — </strong>{item.d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <div id="security" className="trust-strip">
        <div className="trust-inner">
          <h2 className="trust-title">Designed for organizations where getting it wrong isn't an option.</h2>
          <div className="trust-pills">
            {[
              { icon: '🔒', label: 'Bank-level encryption at rest and in transit' },
              { icon: '📋', label: 'Immutable audit log for every action' },
              { icon: '🛡️', label: 'Role-based access controls' },
              { icon: '📄', label: 'Legal-grade document storage' },
              { icon: '⚖️', label: 'NCAA & state compliance tracking' },
              { icon: '🔐', label: 'SOC 2 alignment' },
            ].map(pill => (
              <div className="trust-pill" key={pill.label}>
                <span>{pill.icon}</span>
                {pill.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <section id="contact">
        <div className="cta-section">
          <h2 className="cta-title">Less risk.<br />More clarity.<br />Full control.</h2>
          <div>
            <p className="cta-body">
              Arch replaces the spreadsheets, scattered contracts, and manual reconciliation that put your organization at risk. The first conversation takes 30 minutes.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <a href="#contact" className="btn-primary" onClick={() => setShowDemo(true)}>
                Request a Demo
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7h10M7 2l5 5-5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
            <p className="cta-note">No commitment required. We'll walk you through the platform.</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: '1px solid var(--border)' }}>
        <div className="footer">
          <div className="footer-logo">Arch</div>
          <ul className="footer-links">
            <li><a href="mailto:hello@arch.com">Contact</a></li>
            <li><a href="/privacy">Privacy</a></li>
            <li><a href="/security">Security</a></li>
          </ul>
          <div className="footer-copy">© 2025 Arch</div>
        </div>
      </footer>

      <DemoForm isOpen={showDemo} onClose={() => setShowDemo(false)} />
    </div>
  );
};

export default Arch;
